<template>
  <div id="loginScreen" class="full-screen-overlay">
    <form class="auth-box card" @submit.prevent="handleSubmit">
      <h1>Kaelu AI</h1>
      <h2>{{ isLogin ? 'Log in to your account' : 'Create a new account' }}</h2>

      <!-- messages -->
      <p v-if="error" class="error-message">{{ error }}</p>
      <p v-if="success" class="success-message">{{ success }}</p>

      <div class="input-group">
        <label for="email">Email address</label>
        <div class="input-field-wrapper">
          <span class="material-symbols-rounded">mail</span>
          <input v-model="email" id="email" type="email" placeholder="Enter your email address" autocomplete="username" />
        </div>
      </div>

      <div class="input-group" v-if="!resetMode">
        <label for="password">Password</label>
        <div class="input-field-wrapper">
          <span class="material-symbols-rounded">lock</span>
          <input v-model="password" :type="showPassword ? 'text' : 'password'" id="password" placeholder="Enter your password" autocomplete="current-password" />
          <span class="material-symbols-rounded password-toggle" @click="togglePasswordVisibility">{{ showPassword ? 'visibility' : 'visibility_off' }}</span>
        </div>
      </div>

      <div class="auth-options" v-if="!resetMode && isLogin">
        <label class="checkbox-container">
          <input type="checkbox" id="keepLoggedIn" checked>
          Keep me logged in
          <span class="material-symbols-rounded">info</span>
        </label>

        <a href="#" class="forgot-password-link" @click.prevent="resetMode = true">Reset password</a>
      </div>

      <!--  submit button -->
      <button type="submit" class="btn btn-ubisoft-primary" :disabled="loading">
        {{ loading ? 'Please wait...' : resetMode ? 'Send Reset Email' : isLogin ? 'Log In' : 'Sign Up' }}
      </button>

      <div class="auth-separator">
        <button @click.prevent="toggleMode" class="btn auth-separator-link">
          {{ isLogin ? 'Create an account' : 'Already have an account?' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { auth } from '@/firebase';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';

import { mapFirebaseError } from '@/utils';

const email = ref('');
const password = ref('');
const error = ref('');
const success = ref('');
const loading = ref(false);
const isLogin = ref(true);
const isSignup = ref(false);
const resetMode = ref(false);
const showPassword = ref(false);

const router = useRouter();
const authStore = useAuthStore();

watchEffect(() => {
  if (authStore.user) {
    router.push('/dashboard');
  }
});

const handleSubmit = async () => {
  error.value = '';
  success.value = '';
  loading.value = true;

  try {
    if (resetMode.value) {
      if (!email.value) throw new Error('auth/missing-email');
      await sendPasswordResetEmail(auth, email.value);
      success.value = 'Password reset email sent.';
      resetMode.value = false;
    } else if (isLogin.value) {
      await authStore.login(email.value, password.value);
      router.push('/dashboard');
    } else {
      // await createUserWithEmailAndPassword(auth, email.value, password.value);

      await authStore.registerUser(email.value, password.value);

      success.value = 'Account created. You can now log in.';
      isLogin.value = true;
    }
  } catch (err) {
    error.value = mapFirebaseError(err.code || err.message);
  } finally {
    loading.value = false;
  }
};

const toggleMode = () => {
  isLogin.value = !isLogin.value;
  resetMode.value = false;
  error.value = '';
  success.value = '';
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

// need to add confirm password field for signup and date of birth field
</script>
